import { useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Grid } from "@/comn/components";
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useModal, useStore, useToast, usePopup, useGrid, usePage } from "@/comn/hooks"; // hook
import {
    BASE,
    URLS,
    APIS,
    SG_HS_MGMT_LST,
    SF_HS_MGMT_SRCH,
} from "@/tz/go/egaz/osci/ptl/info/hscd/services/OsciHsCdInfoService"; // 서비스

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * APC Management List
 * !== APC 관리 목록 ==!
 */
export const OSCI0110001Q = (props: any) => {
    /*
     * @ 변수에 대한 주석 (기본 Hook 제외)
     * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
     * @ 기본 한줄 주석 형태를 사용
     * @ 포맷
     * @ [지환부호시작] [한글변수명] [지환부호종료]
     *
     * 예시) const pgeUid = "CGME0411001Q"; // Page Unique identifier !== 화면 고유 식별자 ==!
     * 선언과 동시에 전개가 이루어 지는 경우 변수의 위쪽에 작성
     */

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */

    const { pgeUid, menuId } = usePage(); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    let [query, setQuery] = useSearchParams();

    /*
     * @ 화면에서 사용하는 form 객체를 선언
     * @ 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * @ 검색 조건일 경우 구분을 위해 폼 이름 뒤에 Srch
     * @ const form = {
     *      [form 이름] : useForm({
     *          defaultSchema: [form의 schema 구조],
     *          defaultValues: [form의 기본 값] || {},
     *      }),
     *      ...
     * }
     *
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // APC Management Search !== APC 관리 검색 ==!
        clreHsMgmtSrch: useForm({
            defaultSchema: SF_HS_MGMT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * @ 화면에서 사용하는 grid 객체를 선언
     * @ 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * @ 그리드 변수명 뒤에 List (List) 를 붙여 구분
     * @ const grid = {
     *      [grid 이름] : useWijmo({
     *          defaultSchema: [grid의 schema 구조],
     *          page: [페이지 번호, 기본 0부터 시작],
                size: [한 페이지에 보여줄 데이터 갯수],
     *      }),
     *      ...
     * }
     * 
     * Declaration Grid
     * !== 그리드 정의 ==! 
     */
    const grid = {
        // APC Management List !== APC 관리 목록 ==!
        hsMgmtList: useGrid({
            defaultSchema: SG_HS_MGMT_LST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * 화면에서 사용하는 api 호출 객체를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * Back-End Controller 메소드명과 동일하게 작성(Back-End 개발 가이드 참조)
     * 
     * 다건 조회    get         getRpckItmAppList
     * 
     * const fetch = {
     *      [fetch 이름] : useFetch({
     *          api: [fetch 실행 api, service 파일의 APIS 에 기재],
     *          enabled: [[fetch 가 실행되기 위한 조건]],
                key: [[fetch 에서 변화를 감지하여 자동실행할 변수]],
                onSuccess : [fetch 실행 성공시 실행],
                onError : [fetch 실행 실패시 실행],
                showToast: [성공/실패 결과 Toast메세지 표시 여부],
     *      }),
     *      ...
     * }
     * 
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get HS Management List !== HS 관리 목록 조회 ==!
        getClreHsMgmtPageList: useFetch({
            api: (page = grid.hsMgmtList.page) => {
                return APIS.getClreHsMgmtPageList(form.clreHsMgmtSrch.getValues(), page, grid.hsMgmtList.size);
            },
            enabled: comnUtils.isEmpty(form.clreHsMgmtSrch.errors) && form.clreHsMgmtSrch.isSubmitted,
            key: [grid.hsMgmtList.page, grid.hsMgmtList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.clreHsMgmtSrch.getValues(),
                    page: grid.hsMgmtList.page,
                    size: grid.hsMgmtList.size,
                });
            },
            onError: () => {},
            showToast: true,
        }),
    };

    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : hs 관리 그리드 클릭
     *      ...
     * }
     *
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get hs Management List !== hs 관리 목록 조회 ==!
        getClreHsMgmtPageList: () => {
            form.clreHsMgmtSrch.handleSubmit(
                (data) => {
                    if (query.get("before") === "Y") {
                        grid.hsMgmtList.setPage(grid.hsMgmtList.page);
                        fetch.getClreHsMgmtPageList.fetch(grid.hsMgmtList.page);
                    } else {
                        grid.hsMgmtList.setPage(0);
                        fetch.getClreHsMgmtPageList.fetch(0);
                    }
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },

        //HS Code Value Popup Process !== 검색조건 HS 돋보기 클릭 ==!
        click_Btn_AddGridHsMgmtList: () => {
            modal.openModal({
                url: comnEnvs.popup.hsCd,
                // modal 창에서 postMessage 메서드 실행시 call back 함수
                callback: (data) => {
                    // form에 값 세팅
                    form.clreHsMgmtSrch.setValue("hsCd", data.code);
                    form.clreHsMgmtSrch.setValue("hsDesc", data.label);
                    // modal 창 닫기
                    modal.closeModal();
                },
            });
        },
    };

    //검색조건 적용여부 값 세팅
    const optionYn = [
        { label: "Y", value: "Y" },
        { label: "N", value: "N" },
    ];

    const render = {
        click_Grid_HsDtlSelc: {
            head: {},
            cell: {
                hsCd: (props: any) => {
                    const { binding, rowValues, value } = props;
                    return <Link to={`${URLS.osci0110002q}/${rowValues.hsCd}`}>{`${rowValues.hsCd}`}</Link>;
                },
            },
            edit: {},
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.getClreHsMgmtPageList();
    }, []);

    /*
     * @ 라벨 생성(명명 규칙)
     * @ prefix
     * @ 일반라벨 : L_
     * @ 버튼 : B_
     * @ 제목 : T_
     * @ 메세지 : msg.00000 [msg.숫자5자리] , 00000 ~ 10000 까지는 공통영역
     * @ 매세지를 제외한 라벨은 메타 단어, 용어 조합, 메세지는 숫자 5자리 일련번호로 정의
     * @ 대문자로 표시
     * @ 단어와 단어 사이 용어와 용어 사이는 "_" 로 구분
     */

    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                {/* HS !== HS ==!  */}
                                <Group.Control
                                    {...form.clreHsMgmtSrch.schema.hsCd}
                                    rightButton={{
                                        icon: "search",
                                        onClick: () => {
                                            handler.click_Btn_AddGridHsMgmtList();
                                        },
                                    }}
                                />
                                <Group.Control {...form.clreHsMgmtSrch.schema.hsDesc} />
                            </Group.Row>
                            <Group.Row>
                                {/* Processing Status !== 처리상태 ==!  */}
                                <Group.Control
                                    {...form.clreHsMgmtSrch.schema.appdYn}
                                    options={optionYn}
                                ></Group.Control>
                            </Group.Row>
                        </Group.Section>

                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role={"reset"}
                                    onClick={() => {
                                        form.clreHsMgmtSrch.reset();
                                        form.clreHsMgmtSrch.setValues({ hsCd: "", hsDesc: "", appdYn: "" });
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right>
                                <Button
                                    role={"search"}
                                    onClick={() => {
                                        handler.getClreHsMgmtPageList();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>

            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Button
                                role={"excelDown"}
                                onClick={(props: any) => {
                                    grid.hsMgmtList.exportExcel({
                                        excelName: "HS Code List",
                                        sheetName: "HS Code List",
                                    });
                                }}
                            ></Button>
                        </Layout.Left>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.hsMgmtList.grid}
                        data={fetch.getClreHsMgmtPageList.data?.hsMgmtList}
                        render={render.click_Grid_HsDtlSelc}
                    />
                </Group.Body>
            </Group>
        </Page>
    );
};
